/**
 * Twenty-eight classification rules for Meridian Associates.
 * Priorities 1-28 (lower = higher priority).
 *
 * Each rule has 1-4 conditions, mixes ConditionType {URL, APP, SITE, TITLE,
 * IP, DOCUMENT} and ConditionOperator {is, starts with, ends with, contains,
 * matches}. Mix of enabled/disabled. Project refs point at flat PROJECTS ids.
 *
 * Task 193: every fixture carries `mode` (`'all'` is AND across conditions,
 * the engine default; `'any'` is OR). At least one fixture (rule-002) uses
 * `mode: 'any'` so the OR-prefix render path is exercised in mockup mode
 * without a backend.
 */

import type { Rule } from '@/types/entities';

export const RULES = [
  {
    id: 'rule-001',
    priority: 1,
    name: 'Office 365 — Meridian',
    projectId: 'prj-meridian-restructuring',
    status: 'enabled',
    mode: 'all',
    conditions: [{ type: 'URL', operator: 'contains', value: 'meridian' }],
  },
  {
    id: 'rule-002',
    priority: 2,
    name: 'Westlaw or patent (Any)',
    projectId: 'prj-techventure-patents',
    status: 'enabled',
    // Demonstrates the 'any' (OR) joiner in mockup mode — task 193 D1.
    mode: 'any',
    conditions: [
      { type: 'APP', operator: 'is', value: 'Westlaw' },
      { type: 'TITLE', operator: 'contains', value: 'patent' },
    ],
  },
  {
    id: 'rule-003',
    priority: 3,
    name: 'EDGAR — Apex Filings',
    projectId: 'prj-meridian-mna',
    status: 'enabled',
    mode: 'all',
    conditions: [
      { type: 'URL', operator: 'matches', value: 'sec\\.gov/cgi-bin/browse-edgar.*apex' },
    ],
  },
  {
    id: 'rule-004',
    priority: 4,
    name: 'Bloomberg — Pinnacle',
    projectId: 'prj-pinnacle-regulatory',
    status: 'enabled',
    mode: 'all',
    conditions: [
      { type: 'APP', operator: 'is', value: 'Bloomberg Terminal' },
      { type: 'SITE', operator: 'contains', value: 'pinnacle' },
    ],
  },
  {
    id: 'rule-005',
    priority: 5,
    name: 'DocuSign — Anderson Estate',
    projectId: 'prj-anderson-estate',
    status: 'enabled',
    mode: 'all',
    conditions: [
      { type: 'SITE', operator: 'is', value: 'docusign.com' },
      { type: 'DOCUMENT', operator: 'contains', value: 'anderson' },
    ],
  },
  {
    id: 'rule-006',
    priority: 6,
    name: 'Stellar Contract Drafts',
    projectId: 'prj-stellar-contract',
    status: 'enabled',
    mode: 'all',
    conditions: [
      { type: 'APP', operator: 'is', value: 'Microsoft Word' },
      { type: 'DOCUMENT', operator: 'starts with', value: 'Stellar-' },
    ],
  },
  {
    id: 'rule-007',
    priority: 7,
    name: 'Quantum Patent Files',
    projectId: 'prj-quantum-ip-licensing',
    status: 'enabled',
    mode: 'all',
    conditions: [
      { type: 'DOCUMENT', operator: 'contains', value: 'quantum' },
      { type: 'DOCUMENT', operator: 'ends with', value: '.pdf' },
    ],
  },
  {
    id: 'rule-008',
    priority: 8,
    name: 'Legal Aid Portal',
    projectId: 'prj-community-probono',
    status: 'enabled',
    mode: 'all',
    conditions: [{ type: 'SITE', operator: 'contains', value: 'legalaid' }],
  },
  {
    id: 'rule-009',
    priority: 9,
    name: 'Office VPN',
    projectId: 'prj-firm-admin',
    status: 'enabled',
    mode: 'all',
    conditions: [{ type: 'IP', operator: 'starts with', value: '10.42.' }],
  },
  {
    id: 'rule-010',
    priority: 10,
    name: 'Meridian Outlook',
    projectId: 'prj-meridian-restructuring',
    status: 'enabled',
    mode: 'all',
    conditions: [
      { type: 'APP', operator: 'is', value: 'Microsoft Outlook' },
      { type: 'TITLE', operator: 'contains', value: 'Meridian Capital' },
    ],
  },
  {
    id: 'rule-011',
    priority: 11,
    name: 'TechVenture Slack',
    projectId: 'prj-techventure-patents',
    status: 'enabled',
    mode: 'all',
    conditions: [
      { type: 'APP', operator: 'is', value: 'Slack' },
      { type: 'TITLE', operator: 'contains', value: 'techventure' },
    ],
  },
  {
    id: 'rule-012',
    priority: 12,
    name: 'Internal Wiki',
    projectId: 'prj-knowledge-mgmt',
    status: 'enabled',
    mode: 'all',
    conditions: [{ type: 'SITE', operator: 'ends with', value: 'meridian-associates.com' }],
  },
  {
    id: 'rule-013',
    priority: 13,
    name: 'LMS Training',
    projectId: 'prj-internal-training',
    status: 'enabled',
    mode: 'all',
    conditions: [
      { type: 'URL', operator: 'contains', value: 'lms.meridian-associates.com' },
      { type: 'TITLE', operator: 'contains', value: 'course' },
    ],
  },
  {
    id: 'rule-014',
    priority: 14,
    name: 'IT Ticketing',
    projectId: 'prj-firm-admin',
    status: 'enabled',
    mode: 'all',
    conditions: [{ type: 'SITE', operator: 'is', value: 'servicenow.com' }],
  },
  {
    id: 'rule-015',
    priority: 15,
    name: 'Remote VPN (deprecated)',
    projectId: 'prj-firm-admin',
    status: 'disabled',
    mode: 'all',
    conditions: [{ type: 'IP', operator: 'matches', value: '^172\\.16\\.\\d+\\.\\d+$' }],
  },
  {
    id: 'rule-016',
    priority: 16,
    name: 'Old CRM System',
    projectId: 'prj-firm-admin',
    status: 'disabled',
    mode: 'all',
    conditions: [
      { type: 'URL', operator: 'starts with', value: 'https://oldcrm.meridian-associates.com' },
    ],
  },
  {
    id: 'rule-017',
    priority: 17,
    name: 'Deprecated Portal',
    projectId: 'prj-firm-admin',
    status: 'disabled',
    mode: 'all',
    conditions: [{ type: 'SITE', operator: 'contains', value: 'legacy-portal' }],
  },
  {
    id: 'rule-018',
    priority: 18,
    name: 'Test Rule',
    projectId: 'prj-internal-training',
    status: 'disabled',
    mode: 'all',
    conditions: [{ type: 'TITLE', operator: 'contains', value: 'test' }],
  },
  {
    id: 'rule-019',
    priority: 19,
    name: 'Apex Acquisition — EDGAR',
    projectId: 'prj-apex-acquisition',
    status: 'enabled',
    mode: 'all',
    conditions: [
      { type: 'SITE', operator: 'is', value: 'sec.gov' },
      { type: 'URL', operator: 'contains', value: 'apex' },
    ],
  },
  {
    id: 'rule-020',
    priority: 20,
    name: 'Pinnacle Tax — Lexis',
    projectId: 'prj-pinnacle-tax',
    status: 'enabled',
    mode: 'all',
    conditions: [
      { type: 'SITE', operator: 'is', value: 'lexisnexis.com' },
      { type: 'TITLE', operator: 'contains', value: 'pinnacle' },
      { type: 'TITLE', operator: 'contains', value: 'tax' },
    ],
  },
  {
    id: 'rule-021',
    priority: 21,
    name: 'Harmon Litigation — PACER',
    projectId: 'prj-harmon-litigation',
    status: 'enabled',
    mode: 'all',
    conditions: [
      { type: 'SITE', operator: 'is', value: 'pacer.gov' },
      { type: 'DOCUMENT', operator: 'contains', value: 'harmon' },
    ],
  },
  {
    id: 'rule-022',
    priority: 22,
    name: 'Pacific VC — Formation Docs',
    projectId: 'prj-pacific-vc',
    status: 'enabled',
    mode: 'all',
    conditions: [
      { type: 'DOCUMENT', operator: 'starts with', value: 'PacificVC-' },
      { type: 'DOCUMENT', operator: 'ends with', value: '.docx' },
    ],
  },
  {
    id: 'rule-023',
    priority: 23,
    name: 'Northwood RE — Title Search',
    projectId: 'prj-northwood-real-estate',
    status: 'enabled',
    mode: 'all',
    conditions: [
      { type: 'URL', operator: 'contains', value: 'titlesearch' },
      { type: 'TITLE', operator: 'contains', value: 'Northwood' },
    ],
  },
  {
    id: 'rule-024',
    priority: 24,
    name: 'Crestview Tax — TaxJar',
    projectId: 'prj-crestview-tax-planning',
    status: 'enabled',
    mode: 'all',
    conditions: [{ type: 'SITE', operator: 'ends with', value: 'taxjar.com' }],
  },
  {
    id: 'rule-025',
    priority: 25,
    name: 'Community Legal — Intake Form',
    projectId: 'prj-community-probono',
    status: 'enabled',
    mode: 'all',
    conditions: [
      { type: 'URL', operator: 'matches', value: 'legalaid\\.org/intake/\\d+' },
      { type: 'APP', operator: 'is', value: 'Chrome' },
    ],
  },
  {
    id: 'rule-026',
    priority: 26,
    name: 'Quantum FDA — Submission Portal',
    projectId: 'prj-quantum-fda',
    status: 'enabled',
    mode: 'all',
    conditions: [{ type: 'SITE', operator: 'is', value: 'fda.gov' }],
  },
  {
    id: 'rule-027',
    priority: 27,
    name: 'TechVenture IP Strategy — Notion',
    projectId: 'prj-techventure-ip-strategy',
    status: 'enabled',
    mode: 'all',
    conditions: [
      { type: 'APP', operator: 'is', value: 'Notion' },
      { type: 'TITLE', operator: 'contains', value: 'IP Strategy' },
    ],
  },
  {
    id: 'rule-028',
    priority: 28,
    name: 'Old Compliance Rule',
    projectId: 'prj-apex-compliance',
    status: 'disabled',
    mode: 'all',
    conditions: [
      {
        type: 'URL',
        operator: 'starts with',
        value: 'https://compliance-v1.meridian-associates.com',
      },
      { type: 'TITLE', operator: 'contains', value: 'Apex' },
    ],
  },
] as const satisfies readonly Rule[];
